import React, { useState, useEffect } from 'react';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import classNames from 'classnames';
import EditableSection from 'components/v2/EditableSection';
import Select from 'components/v2/select';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import CargomaticTooltip from 'components/v2/Tooltip';
import { useTranslation } from 'react-i18next';
import SessionStore from 'stores/SessionStore';
import { sluggify } from 'utils/localization/utils';
import RadioList from 'components/v2/RadioList';
import FormGroup from 'components/v2/FormGroup';
import Input from 'components/v2/input';
import { humanize } from 'underscore.string';
import { showToast } from 'stores/ToastStore';
import {
  getUserInvoiceSettings,
  upsertUserInvoiceSettings,
  getPendingAccessorialInclusion,
  upsertPendingAccessorialInclusion,
} from 'utils/UserServiceAPIUtils';

import CustomDropdown from 'components/v2/dropdowns/CustomDropdown';
import {
  getDeliveryFormats,
  CHARGES_BY_GROUPING,
} from 'constants/InvoiceDelivery';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { checkPermissonForEditShipperBillingModule } from '../utils';

const NOT_SPECIFIED = 'NOT_SPECIFIED';

const INVOICE_METHOD_DESCRIPTION_SLUGS = {
  ONE_PER_SHIPMENT: 'ONE_PER_SHIPMENT_DELIVERY_METHOD_DESCRIPTION',
  ONE_PER_SHIPMENT_PER_CHARGE_TYPE:
    'ONE_PER_SHIPMENT_PER_CHARGE_TYPE_DELIVERY_METHOD_DESCRIPTION',
  TWO_PER_SHIPMENT: 'TWO_PER_SHIPMENT_DELIVERY_METHOD_DESCRIPTION',
  UNLIMITED: 'UNLIMITED_DELIVERY_METHOD_DESCRIPTION',
};

const brandingOptions = [
  {
    name: 'Cargomatic',
    slug: 'cargomatic',
    localizationSlug: 'CARGOMATIC',
  },
  {
    name: 'Atlas Marine',
    slug: 'atlas_marine',
    localizationSlug: 'ATLAS_MARINE',
  },
  {
    name: 'West End',
    slug: 'west_end',
    localizationSlug: 'WEST_END',
  },
  {
    name: 'Three Rivers',
    slug: 'three_rivers',
    localizationSlug: 'THREE_RIVERS',
  },
  {
    name: 'xlr_8',
    slug: 'xlr_8',
    localizationSlug: 'XLR_8',
  },
  {
    name: 'BP transportation',
    slug: 'bp_transportation',
    localizationSlug: 'BP_TRANSPORTATION',
  },
];

const invoiceIdOptions = [
  {
    name: 'Suffix',
    slug: 'suffix',
    localizationSlug: 'PREVIOUS_ID_WITH_SUFFIX',
  },
  {
    name: 'Unique Id',
    slug: 'unique_id',
    localizationSlug: 'UNIQUE_ID',
  },
];

const getInvoicePages = (pageSetup = []) => {
  return pageSetup.find((p = {}) => p.shipment_type === 'drayage')?.page_count;
};

const getInvoiceGroupingOptions = (t) => {
  return [
    {
      id: 'ONE_PER_SHIPMENT',
      name: 'One Invoice per Shipment',
      description: () => {
        return (
          <span>{t(INVOICE_METHOD_DESCRIPTION_SLUGS.ONE_PER_SHIPMENT)}</span>
        );
      },
    },
    {
      id: 'ONE_PER_SHIPMENT_PER_CHARGE_TYPE',
      name: 'One invoice per Shipment per Charge Type',
      description: () => {
        return (
          <span>
            {t(
              INVOICE_METHOD_DESCRIPTION_SLUGS.ONE_PER_SHIPMENT_PER_CHARGE_TYPE,
            )}
          </span>
        );
      },
    },
    {
      id: 'TWO_PER_SHIPMENT',
      name: 'Two Invoices per Shipment',
      description: () => {
        return (
          <span>{t(INVOICE_METHOD_DESCRIPTION_SLUGS.TWO_PER_SHIPMENT)}</span>
        );
      },
    },
    {
      id: 'UNLIMITED',
      name: 'Multiple invoices per Shipment',
      description: () => {
        return <span>{t(INVOICE_METHOD_DESCRIPTION_SLUGS.UNLIMITED)}</span>;
      },
    },
  ];
};

const renderInvoiceDeliverySummary = (settings) => {
  const { t } = useTranslation();
  const { invoice_grouping = null, invoice_delivery_configs = [] } =
    settings || {};
  const tooltipContent = invoice_grouping
    ? INVOICE_METHOD_DESCRIPTION_SLUGS[invoice_grouping.toUpperCase()]
    : NOT_SPECIFIED;
  return (
    <>
      <h3 className="mb-4">{t('INVOICE_GROUPING/DELIVERY_FORMAT')}</h3>
      <DescriptionListItem label={t('INVOICE_GROUPING')} className="mb-4">
        <div className="flex items-center space-x-1">
          <span>
            {invoice_grouping
              ? humanize(t(invoice_grouping))
              : t(NOT_SPECIFIED)}
          </span>
          {invoice_grouping && (
            <CargomaticTooltip position="top">
              {t(tooltipContent)}
            </CargomaticTooltip>
          )}
        </div>
      </DescriptionListItem>
      <div className="grid grid-cols-2 gap-4">
        {invoice_delivery_configs.length ? (
          invoice_delivery_configs.map((config) => {
            const chargeLabel = humanize(t(config.charge_type));
            const method = humanize(t(config.invoicing_method));
            return (
              <DescriptionListItem label={chargeLabel} className="">
                {method}
              </DescriptionListItem>
            );
          })
        ) : (
          <DescriptionListItem label={t('DELIVERY_METHOD')} className="">
            {t(NOT_SPECIFIED)}
          </DescriptionListItem>
        )}
      </div>
    </>
  );
};

const renderBillingStatusSummary = (includePendingAccessorials) => {
  const { t } = useTranslation();

  return (
    <>
      <h3 className="mb-4">{t('BILLING_STATUS_EVALUATION')}</h3>
      <div className="grid grid-cols-2 gap-4">
        <DescriptionListItem label={t('INCLUDE_PENDING_ACCESSORIALS')} className="">
          <span>{includePendingAccessorials ? t('YES') : t('NO')}</span>
        </DescriptionListItem>
      </div>
    </>
  );
};

const DrayageInvoiceSettingsSummary = ({
  fields = {},
  invoiceDeliverySettings = {},
  includePendingAccessorials,
}) => {
  const { t } = useTranslation();
  const invoice_pages = getInvoicePages(fields.page_setup);
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <DescriptionListItem label={t('GROUP_LIKE_ITEMS')} className="">
          {fields.group_like_invoice_items}
        </DescriptionListItem>
        <DescriptionListItem label={t('INVOICE_BRANDING')} className="">
          {t(
            brandingOptions.find(
              (o) => o.slug === fields?.drayage_invoice_branding,
            )?.localizationSlug || '',
          )}
        </DescriptionListItem>
        <DescriptionListItem label={t('NUMBER_OF_PAGES')}>
          {invoice_pages}
        </DescriptionListItem>
        <DescriptionListItem label={t('BILLING_TIMEFRAME_DAYS')} className="">
          {fields.drayage_blling_time_frame}
        </DescriptionListItem>
        <DescriptionListItem
          label={t('INVOICE_ID_SETTINGS')}
          info={t('INVOICE_ID_SETTINGS_TOOLTIP')}
          position="top"
          className=""
        >
          {t(sluggify(fields.invoice_id_settings))}
        </DescriptionListItem>
      </div>
      {featureEnabled(CONFIGURED_FEATURES.SSR) && (<><hr className="my-4" /> {renderInvoiceDeliverySummary(invoiceDeliverySettings)}</>)}
      {featureEnabled(CONFIGURED_FEATURES.SSR) && (<><hr className="my-4" /> {renderBillingStatusSummary(includePendingAccessorials)}</>)}
    </>
  );
};

const DrayageInvoiceSettingsForm = ({
  fields,
  onChange,
  saving,
  onSaveShipper,
  onSaveSuccess,
  onSaveFail,
  userId,
  invoiceDeliverySettings = {},
  handleSaveInvoiceDeliverySettings,
  handleInvoiceDeliverySettingsSuccess,
  includePendingAccessorials,
  handleSetIncludePendingAccessorials,
  handleSavePendingAccessorialInclusion,
}) => {
  const { t } = useTranslation();
  const [invoiceGrouping, setInvoiceGrouping] = useState(invoiceDeliverySettings?.invoice_grouping);
  const [invoiceDeliveryMethods, setInvoiceDeliveryMethods] = useState([]);
  const isFinanceAdmin = SessionStore.isFinanceAdmin();
  const isShipperOnboarder = SessionStore.hasShipperOnboardingRole();

  const invoice_pages = getInvoicePages(fields.page_setup);
  const options = [
    {
      value: 'yes',
      name: 'Yes',
      localizationSlug: 'YES',
    },

    {
      value: 'no',
      name: 'No',
      localizationSlug: 'NO',
    },
  ];

  const numberOfPagesOptions = [
    {
      value: '1',
      name: 'One: All Charges together',
      localizationSlug: 'INVOICE_PAGE_OPTION_ONE',
    },
    {
      value: '2',
      name: 'Two: Drayage and FSC on one, All other accessorial on two',
      localizationSlug: 'INVOICE_PAGE_OPTION_TWO',
    },
  ];

  const saveForm = async () => {
    try {
      await onSaveShipper();
      onSaveSuccess(fields);
      await onSaveInvoiceDeliverySettings();
      handleInvoiceDeliverySettingsSuccess();
    } catch (e) {
      onSaveFail();
    }
  };

  const onSaveInvoiceDeliverySettings = async () => {
    if (!invoiceGrouping) return null;
    const mutation = {
      invoice_grouping: invoiceGrouping.id,
      invoice_delivery_configs: [...invoiceDeliveryMethods],
    };
    await handleSaveInvoiceDeliverySettings(mutation);
    await onSavePendingAccessorialInclusion(invoiceGrouping.id);
  };

  const onSavePendingAccessorialInclusion = (invoiceGrouping) => {
    return handleSavePendingAccessorialInclusion(invoiceGrouping);
  };

  useEffect(() => {
    setInitialEditData();
  }, [userId]);

  const setInitialEditData = () => {
    if (!invoiceDeliverySettings) {
      setInvoiceGrouping(null);
      return;
    }
    const { invoice_grouping, invoice_delivery_configs } =
      invoiceDeliverySettings;
    const options = getInvoiceGroupingOptions(t);
    const selectedInvoiceGrouping = invoice_grouping
      ? options.find((option) => option.id.toUpperCase() === invoice_grouping)
      : null;
    setInvoiceGrouping(selectedInvoiceGrouping);

    const methods = invoice_delivery_configs
      ? invoice_delivery_configs.map((r) => {
          return {
            charge_type: r.charge_type,
            invoicing_method: r.invoicing_method,
          };
        })
      : [];
    setInvoiceDeliveryMethods(methods);
  };

  const handleInvoiceDeliverySettingsChange = (change) => {
    setInvoiceDeliveryMethods(change);
  };

  const handleInvoiceGroupingChange = (change) => {
    setInvoiceGrouping(change);
    setInvoiceDeliveryMethods([]);
  };
  const invoiceGroup = invoiceGrouping?.id;
  const canEditIncludePendingAccessorials = invoiceGroup !== 'UNLIMITED' && invoiceGroup !== 'ONE_PER_SHIPMENT';

  useEffect(() => {
    if (saving) {
      saveForm();
    }
  }, [saving]);
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="groupInvoiceItems">{t('GROUP_LIKE_ITEMS')}</Label>
          <Select
            items={options}
            valueKey="name"
            textKey="localizationSlug"
            selected={fields.group_like_invoice_items}
            onSelect={({ name }) => onChange('group_like_invoice_items', name)}
          />
        </div>
        <div>
          <Label htmlFor="bilingTimeFrame">{t('INVOICE_BRANDING')}</Label>
          <Select
            items={brandingOptions}
            valueKey="slug"
            textKey="localizationSlug"
            selected={fields.drayage_invoice_branding}
            onSelect={({ slug }) => onChange('drayage_invoice_branding', slug)}
          />
        </div>
        <div className="">
          <Label htmlFor="bilingTimeFrame">{t('BILLING_TIMEFRAME_DAYS')}</Label>
          <TextField
            value={fields.drayage_blling_time_frame}
            onChange={(val) => onChange('drayage_blling_time_frame', val)}
          />
        </div>
        <div className="col-span-2">
          <Label>{t('NUMBER_OF_PAGES')}</Label>
          <Select
            items={numberOfPagesOptions}
            valueKey="value"
            textKey="localizationSlug"
            className="z-10"
            selected={`${invoice_pages}`}
            onSelect={(option) => {
              onChange('invoice_pages', option?.value);
            }}
          />
        </div>
        <div>
          {isFinanceAdmin && (
            <Select
              items={invoiceIdOptions}
              label={t('INVOICE_ID')}
              info={t('INVOICE_ID_SETTINGS_TOOLTIP')}
              valueKey="slug"
              textKey="localizationSlug"
              selected={fields.invoice_id_settings}
              onSelect={({ slug }) => onChange('invoice_id_settings', slug)}
            />
          )}
        </div>
      </div>
      {featureEnabled(CONFIGURED_FEATURES.SSR) && isShipperOnboarder && (
        <EditInvoiceDeliverySettingsForm
          invoiceDeliverySettings={invoiceDeliverySettings}
          handleInvoiceDeliverySettingsChange={
            handleInvoiceDeliverySettingsChange
          }
          invoiceGrouping={invoiceGrouping}
          setInvoiceDeliveryMethods={setInvoiceDeliveryMethods}
          invoiceDeliveryMethods={invoiceDeliveryMethods}
          setInvoiceGrouping={handleInvoiceGroupingChange}
        />
      )}
      {featureEnabled(CONFIGURED_FEATURES.SSR) && canEditIncludePendingAccessorials && isShipperOnboarder && (
        <EditBillingStatusEvaluationForm
          includePendingAccessorials={includePendingAccessorials}
          invoiceGrouping={invoiceDeliverySettings?.invoice_grouping}
          handleSetIncludePendingAccessorials={handleSetIncludePendingAccessorials}
        />
      )}
    </>
  );
};
const EditInvoiceDeliverySettingsForm = ({
  invoiceDeliverySettings,
  handleInvoiceDeliverySettingsChange,
  setInvoiceDeliveryMethods,
  setInvoiceGrouping,
  invoiceDeliveryMethods = [],
  invoiceGrouping,
}) => {
  const { t } = useTranslation();
  const deliveryFormatOptions = getDeliveryFormats(t);

  const handleInvoiceDeliveryGroupingChange = (method) => {
    setInvoiceGrouping(method);
  };

  const handleDeliveryMethodChange = (change, type) => {
    const method = change.value;
    const previousMethods = invoiceDeliveryMethods.filter(
      (m) => m.charge_type.toUpperCase() !== type.toUpperCase(),
    );
    const newMethod = {
      charge_type: type.toUpperCase(),
      invoicing_method: method.toUpperCase(),
    };
    const changedMethods = [...previousMethods, newMethod];
    setInvoiceDeliveryMethods([...previousMethods, newMethod]);
    handleInvoiceDeliverySettingsChange(changedMethods);
  };

  const getDeliveryMethodCharges = (grouping) => {
    if (!grouping) return null;
    const chargeTypes = CHARGES_BY_GROUPING[grouping.toUpperCase()];
    return chargeTypes || null;
  };

  const renderDeliveryMethodOptions = (grouping) => {
    const chargeTypes = getDeliveryMethodCharges(grouping);
    if (!chargeTypes) {
      return null;
    }
    return (
      <div className="grid grid-cols-2 gap-4">
        {chargeTypes.map((chargeType) => {
          const label = humanize(t(chargeType));
          const currentValue = invoiceDeliveryMethods.find(
            (i) => i.charge_type === chargeType.toUpperCase(),
          );
          const selected = currentValue
            ? deliveryFormatOptions.find(
                (o) => o.value.toUpperCase() === currentValue.invoicing_method,
              )
            : null;
          return (
            <FormGroup label={label} className="">
              <CustomDropdown
                label=""
                options={deliveryFormatOptions}
                onChange={(change) =>
                  handleDeliveryMethodChange(change, chargeType)
                }
                value={selected}
                nameKey="name"
                valueKey="value"
              />
            </FormGroup>
          );
        })}
      </div>
    );
  };
  const invoiceGroupingOptions = getInvoiceGroupingOptions(t);
  const selectedGrouping = invoiceGrouping
    ? invoiceGroupingOptions.find((option) => option.id === invoiceGrouping.id)
    : null;
  return (
    <>
      <hr className="my-4" />
      <FormGroup label={t('INVOICE_GROUPING')} className="mt-4 col-span-full">
        <RadioList
          label={t('INVOICE_GROUPING')}
          options={invoiceGroupingOptions}
          selected={selectedGrouping}
          onSelect={(method) => handleInvoiceDeliveryGroupingChange(method)}
        />
      </FormGroup>
      <label className="mt-4 mb-2">{t('INVOICE_DELIVERY_FORMATS')}</label>
      {selectedGrouping && renderDeliveryMethodOptions(invoiceGrouping.id)}
    </>
  );
};

const EditBillingStatusEvaluationForm = ({ includePendingAccessorials, invoiceGrouping, handleSetIncludePendingAccessorials }) => {
  const { t } = useTranslation();

  return (
    <>
      <hr className="my-4" />
      <FormGroup label={t('BILLING_STATUS_EVALUATION')} className="mt-4 col-span-full">
        <div className="flex items-center space-x-1">
          <Input
            className="my-4"
            type="checkbox"
            label={t('INCLUDE_PENDING_ACCESSORIALS')}
            checked={includePendingAccessorials}
            onChange={handleSetIncludePendingAccessorials}
          />
          <CargomaticTooltip position="top">
            {t('BILLING_STATUS_EVALUATION_DESCRIPTION')}
          </CargomaticTooltip>
        </div>
      </FormGroup>
    </>
  )
};

const DrayageInvoiceSettings = ({ data, className, onUpdateShipper }) => {
  const [initialData, setInitialData] = useState(data);
  const [drayageInvoiceSettingsData, setDrayageInvoiceSettingsData] =
    useState(data);
  const [invoiceDeliverySettings, setInvoiceDeliverySettings] = useState(null);
  const [includePendingAccessorials, setIncludePendingAccessorials] = useState(true);

  const { t } = useTranslation();

  const getInitData = () => {
    if (data) {
      setInitialData(data);
      setDrayageInvoiceSettingsData(data);
    }
  };

  const onChange = (field, value) => {
    if (field === 'invoice_pages') {
      setDrayageInvoiceSettingsData((prev) => {
        let page_setup = prev.page_setup || [];
        const updatedPageSetup = page_setup.map((p) => {
          if (p.shipment_type === 'drayage') {
            p.page_count = value;
          }
          return p;
        });
        return { ...prev, page_setup: updatedPageSetup };
      });
    } else {
      setDrayageInvoiceSettingsData((prev) => ({ ...prev, [field]: value }));
    }
  };
  const handleSave = async () => {
    const updates = {};
    Object.keys(initialData).forEach((key) => {
      if (!(initialData[key] === drayageInvoiceSettingsData[key])) {
        updates[key] = drayageInvoiceSettingsData[key];
      }
      if (key === 'page_setup') {
        delete updates.page_setup;
        updates.invoice_preferences = {
          page_setup: drayageInvoiceSettingsData[key],
        };
      }
    });
    if (Object.keys(updates).length) {
      const payload = { ...updates };
      if (updates.group_like_invoice_items) {
        payload.group_like_invoice_items =
          updates.group_like_invoice_items === 'Yes';
      }
      await onUpdateShipper(payload);
    }
  };

  const handleAfterSave = (onSaveSuccess) => {
    return (updatedData = {}) => {
      setInitialData((prev) => ({ ...prev, ...updatedData }));
      onSaveSuccess();
    };
  };

  const handleInvoiceDeliverySettingsSuccess = () => {
    fetchUserBillingSettings();
  };

  const handleSaveInvoiceDeliverySettings = async (mutation) => {
    try {
      await upsertUserInvoiceSettings(data.userId, mutation);
    } catch (e) {
      showToast(t('ERROR_SAVING_INVOICE_DELIVERY_SETTINGS'), null, 'error');
      console.log('error saving invoice settings', e.message);
    }
  };

  const determinePendingAccessorialsInclusionToSave = (invoiceGrouping) => {
    if (invoiceGrouping === 'UNLIMITED') {
      return false;
    } else if (invoiceGrouping === 'ONE_PER_SHIPMENT') {
      return true;
    } else {
      return includePendingAccessorials;
    }
  }

  const handleSavePendingAccessorialInclusion = async (invoiceGrouping) => {
    try {
      const statusToSave = determinePendingAccessorialsInclusionToSave(invoiceGrouping);
      await upsertPendingAccessorialInclusion(data.userId, statusToSave);
    } catch (e) {
      showToast(t('ERROR_SAVING_PENDING_ACCESSORIAL_INCLUSION'), null, 'error');
      console.error('error saving pending accessorial inclusion settings', e.message);
    }
  };

  useEffect(() => {
    fetchUserBillingSettings();
  }, [data.userId]);

  const determineIncomingPendingAccessorialsInclusion = (invoiceGrouping, pendingAccessorialInclusion) => {
    if (invoiceGrouping === 'UNLIMITED') {
      return false;
    } else if (invoiceGrouping === 'ONE_PER_SHIPMENT') {
      return true;
    } else {
      return pendingAccessorialInclusion;
    }
  }

  const fetchUserBillingSettings = async () => {
    try {
      const result = await getUserInvoiceSettings(data.userId);
      const settings = result?.data?.getInvoiceSetting?.invoice_setting;
      const pendingAccessorialsInclusionStatus = await getPendingAccessorialInclusion(data.userId);
      const pendingAccessorialInclusion = pendingAccessorialsInclusionStatus.data?.getBillReadyStatusCalculation?.include_pending_accessorials_in_calculation;
      const defaultPendingAccessorialsInclusion = determineIncomingPendingAccessorialsInclusion(settings?.invoice_grouping, pendingAccessorialInclusion);
      setInvoiceDeliverySettings(settings);
      setIncludePendingAccessorials(defaultPendingAccessorialsInclusion);
    } catch (e) {
      console.log('Error fetching userInvoiceSettings', e.message);
    }
  };

  useEffect(() => {
    getInitData();
  }, []);

  return (
    <EditableSection
      title={t('INVOICE')}
      className={classNames(className)}
      canEdit={checkPermissonForEditShipperBillingModule()}
    >
      {(editing, saving, onSaveSuccess, onSaveFail) => (
        <>
          {!editing && (
            <DrayageInvoiceSettingsSummary
              fields={initialData}
              invoiceDeliverySettings={invoiceDeliverySettings}
              includePendingAccessorials={includePendingAccessorials}
            />
          )}
          {editing && (
            <DrayageInvoiceSettingsForm
              fields={drayageInvoiceSettingsData}
              onChange={onChange}
              onSaveShipper={() => handleSave()}
              saving={saving}
              onSaveSuccess={handleAfterSave(onSaveSuccess)}
              onSaveFail={onSaveFail}
              invoiceDeliverySettings={invoiceDeliverySettings}
              userId={data.userId}
              handleSaveInvoiceDeliverySettings={
                handleSaveInvoiceDeliverySettings
              }
              handleInvoiceDeliverySettingsSuccess={
                handleInvoiceDeliverySettingsSuccess
              }
              includePendingAccessorials={includePendingAccessorials}
              handleSetIncludePendingAccessorials={setIncludePendingAccessorials}
              handleSavePendingAccessorialInclusion={handleSavePendingAccessorialInclusion}
            />
          )}
        </>
      )}
    </EditableSection>
  );
};

export default DrayageInvoiceSettings;
